import {
  CustomColumnHeaderToClone,
  CustomColumnItem,
  ID,
  LineItem,
  Lot,
  LotChanges,
  QualQuestion
} from 'types';
import {
  addDefaultsToImportedLot,
  generateQuestionsForLot
} from '@components/bid_request/details/utils';

interface ProcessImportLotParams {
  allSurveyQuestions: QualQuestion[],
  changes: Record<string, LotChanges>,
  deletedQualifyingCustomColumnHeaders?: CustomColumnHeaderToClone[];
  newIncomingLot: Lot,
  newSno: number,
  replaceAll?: boolean;
}

interface ProcessImportLotResult {
  lot: Lot;
  questions: QualQuestion[];
  updatedChanges: Record<string, LotChanges>;
}

/**
 * Processes an imported lot to add defaults and generate questions.
 * Creates a new updated changes object rather than modifying the input.
 *
 * @param props
 * @param props.allSurveyQuestions {QualQuestion[]} - All survey questions available
 * @param props.changes {Record<string, LotChanges>} - The current changes record
 * @param props.deletedQualifyingCustomColumnHeaders {CustomColumnHeaderToClone[]} - Custom Column Headers that were deleted from replaceAll
 * @param props.newIncomingLot {Lot} - The incoming lot data to be processed
 * @param props.newSno {Number} - The new serial number to assign to the lot
 * @param props.replaceAll {boolean} - The flag indicating if this is a replace all operation
 * @returns {ProcessImportLotResult} Object containing the processed lot, generated questions, and updated changes
 * @returns {Lot} Object.lot - The processed lot with defaults added
 * @returns {QualQuestion[]} Object.questions - The generated questions for the lot
 * @returns {Record<string, LotChanges>} Object.updatedChanges - A new changes object with updates
 */
export const processImportedLot = ({
  allSurveyQuestions,
  changes,
  deletedQualifyingCustomColumnHeaders = [],
  newIncomingLot,
  newSno,
  replaceAll = false
}: ProcessImportLotParams): ProcessImportLotResult => {
  const updatedChanges = { ...changes };
  const newLot = addDefaultsToImportedLot(newIncomingLot, newSno);
  const [newLotQuestions, newAppliedStateMap] = generateQuestionsForLot(newLot, allSurveyQuestions);
  const columnHeaders = (newLot.custom_column_headers || [])
    .map(header => [header.id, { ...header }]);
  const existingLotChanges = updatedChanges[newLot.id] || { id: newLot.id };
  const updatedLotChanges: LotChanges = {
    ...existingLotChanges,
    applied_questions_state: newAppliedStateMap,
    custom_column_headers: Object.fromEntries(columnHeaders),
    name: newLot.name,
    sno: newLot.sno,
  };

  if (replaceAll) {
    updatedLotChanges.deleted_qualifying_custom_column_headers = deletedQualifyingCustomColumnHeaders;
  }

  const existingLineItems = updatedLotChanges.line_items || {};
  const newLineItems = { ...existingLineItems };

  (newLot.line_items || []).forEach((item: LineItem) => {
    // Transform line item's custom_column_items from array format to Record format
    // The type system expects custom_column_items to be a Record<ID, CustomColumnItem>
    // (key-value pairs with header IDs as keys) while the incoming data structure
    // provides them as an array. This conversion maintains all the original data
    // while restructuring it to match the expected type definition.
    const formattedItem = {
      ...item,
      custom_column_items: {} as Record<ID, CustomColumnItem>
    };

    // If the original item has custom_column_items as an array, convert it to a record
    if (Array.isArray(item.custom_column_items)) {
      item.custom_column_items.forEach(columnItem => {
        if (columnItem && columnItem.id) {
          formattedItem.custom_column_items[columnItem.id] = columnItem;
        }
      });
    }

    newLineItems[item.id] = formattedItem;
  });

  updatedLotChanges.line_items = newLineItems;
  updatedChanges[newLot.id] = updatedLotChanges;

  return {
    lot: newLot,
    questions: newLotQuestions,
    updatedChanges
  };
};
